import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { operations as docServiceOperations } from 'store/docServiceManager'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { selectors as licenseSelectors } from 'store/licenseManager'
import { isDwbReportPathname } from 'store/licenseManager/reportRouteHelpers'
import { selectors as reportsSelectors } from 'store/reportsManager'
import K8ReportingLeaderTools from './component'

export const K8ReportingLeaderToolsContainer = ({
  buttonText,
  classesCount,
  clearDocumentUrl,
  deleteHistoricalReports,
  deleteLessonProgressReport,
  documentUrl,
  entry,
  fetchDocumentUrl,
  fetchHistoricalReports,
  historicalReportData,
  isFetchingDocumentUrl,
  isFetchingHistoricalReportData,
  isDwbReportRoute,
  isSchoolSummary,
  k8LicenseIdForSite,
  lmsError,
  navigationEvent,
  pendingInvitations,
  selectedGrade,
  selectedProgram,
  selectedSite,
  siteHistoricalReports,
}) => {
  const [
    isDownloadingHistoricalReportData,
    setDownloadingHistoricalReportData,
  ] = useState(false)

  useEffect(() => {
    if (documentUrl) {
      const downloadLink = document.createElement('a')
      downloadLink.href = documentUrl
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      clearDocumentUrl()
    }
  }, [documentUrl])

  useEffect(() => {
    if (!k8LicenseIdForSite || !selectedSite) return
    if (
      !historicalReportData[k8LicenseIdForSite] &&
      !historicalReportData[selectedSite] &&
      !isFetchingHistoricalReportData &&
      !lmsError
    ) {
      fetchHistoricalReports(selectedSite)
    }
  }, [
    fetchHistoricalReports,
    historicalReportData,
    isFetchingHistoricalReportData,
    k8LicenseIdForSite,
    lmsError,
    selectedSite,
  ])

  useEffect(() => {
    const cleanUp = () => {
      deleteHistoricalReports?.()
      deleteLessonProgressReport?.()
    }

    return cleanUp
  }, [deleteHistoricalReports, deleteLessonProgressReport])

  const onClickCurrentYear = () => {
    fetchDocumentUrl({
      siteId: selectedSite,
      program: selectedProgram?.key,
    })
  }

  const historicalReportingOnClick = async (data, filename, url) => {
    const licenseId = null

    const csvdata = lmsSelectors.createLessonReportDownloadData(
      data,
      licenseId,
      selectedSite,
      filename,
      filename?.split('.').pop() || 'csv',
    )

    if (!url && !data) {
      return false
    }

    if (!data) {
      try {
        setDownloadingHistoricalReportData(true)
        const response = await fetch(url)
        const blob = await response.blob()
        const newUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = newUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
        setDownloadingHistoricalReportData(false)

        return true
      } catch (error) {
        console.error('Error downloading file:', error)
        return false
      }
    }

    return csvdata
  }

  return (
    <K8ReportingLeaderTools
      buttonText={buttonText}
      classesCount={classesCount}
      entry={entry}
      error={lmsError}
      historicalReportingOnClick={historicalReportingOnClick}
      historicalReports={siteHistoricalReports}
      isDownloadingHistoricalReportData={isDownloadingHistoricalReportData}
      isDwbReportRoute={isDwbReportRoute}
      isFetchingDocumentUrl={isFetchingDocumentUrl}
      isSchoolSummary={isSchoolSummary}
      navigationEvent={navigationEvent}
      onClickCurrentYear={onClickCurrentYear}
      pendingInvitations={pendingInvitations}
      selectedSite={selectedSite}
    />
  )
}

K8ReportingLeaderToolsContainer.propTypes = {
  buttonText: PropTypes.string,
  classesCount: PropTypes.number,
  clearDocumentUrl: PropTypes.func,
  deleteHistoricalReports: PropTypes.func,
  deleteLessonProgressReport: PropTypes.func,
  documentUrl: PropTypes.string,
  entry: PropTypes.object,
  fetchDocumentUrl: PropTypes.func,
  fetchHistoricalReports: PropTypes.func,
  historicalReportData: PropTypes.object,
  isDwbReportRoute: PropTypes.bool,
  isFetchingDocumentUrl: PropTypes.bool,
  isFetchingHistoricalReportData: PropTypes.bool,
  isSchoolSummary: PropTypes.bool,
  k8LicenseIdForSite: PropTypes.number,
  lmsError: PropTypes.object,
  navigationEvent: PropTypes.func,
  pendingInvitations: PropTypes.number,
  selectedGrade: PropTypes.string,
  selectedProgram: PropTypes.object,
  selectedSite: PropTypes.number,
  siteHistoricalReports: PropTypes.array,
}

const mapStateToProps = (state, { selectedSite: currentSiteId }) => {
  const schoolSummaryReport = reportsSelectors.selectSchoolSummaryReport(state)
  const { pendingInvitations } = schoolSummaryReport || {}
  const { docServiceManager, lmsManager, reportsManager } = state
  const { documentUrl, isFetching: isFetchingDocumentUrl } =
    docServiceManager || {}
  const { historicalReportData, isFetchingHistoricalReportData } =
    lmsManager?.historicalReports || {}
  const {
    k8Reports,
    selectedGrade,
    selectedSite: reportSelectedSite,
    selectedProgram,
  } = reportsManager || {}
  const selectedSite = currentSiteId ?? reportSelectedSite
  const activeAdminSites = licenseSelectors.selectActiveAdminK8Sites(state)

  let k8LicenseIdForSite = null
  const siteData = activeAdminSites?.find(site => site.id === selectedSite)

  const { licenses } = siteData || {}
  const k8License = licenses?.find(l => {
    const expiredLicense = l.isExpired || l.cancelledDate
    if (expiredLicense) return false
    const { programCodes } = l.product
    return (
      (programCodes.includes('K5P') && programCodes.includes('K8P')) ||
      programCodes.includes('MSP')
    )
  })
  k8LicenseIdForSite = k8License?.id

  const siteHistoricalReports = historicalReportData[selectedSite] || []

  const { classesCount } =
    k8Reports?.[selectedSite]?.gradeDetails?.[selectedGrade] || {}
  const isDwbReportRoute = isDwbReportPathname(
    state?.router?.location?.pathname,
  )

  return {
    classesCount,
    documentUrl,
    historicalReportData,
    isDwbReportRoute,
    isFetchingDocumentUrl,
    isFetchingHistoricalReportData,
    k8LicenseIdForSite,
    lmsError: lmsManager.error,
    pendingInvitations,
    selectedGrade,
    selectedProgram,
    selectedSite,
    siteHistoricalReports,
  }
}

const mapDispatchToProps = {
  clearDocumentUrl: docServiceOperations.clearDocumentUrl,
  deleteHistoricalReports: lmsOperations.deleteHistoricalReports,
  deleteLessonProgressReport: lmsOperations.deleteLessonProgressReport,
  fetchDocumentUrl: docServiceOperations.fetchDocumentUrl,
  fetchHistoricalReports: lmsOperations.fetchHistoricalReports,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(K8ReportingLeaderToolsContainer)
